/**
 * MarketingOptInBanner Component
 * Banner shown on the dashboard for users who haven't opted into marketing.
 * Allows them to subscribe and receive 1 free credit (analysis).
 */

import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Gift, X, Loader2 } from 'lucide-react';
import { useMutation } from '@tanstack/react-query';
import { useAuthStore } from '@/stores/authStore';
import { optInService } from '@/services/api/optIn';
import { authService } from '@/services/api/auth';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';

const DISMISS_KEY = 'marketing-opt-in-banner-dismissed';

export function MarketingOptInBanner() {
  const { t } = useTranslation();
  const { user } = useAuthStore();
  const [consent, setConsent] = useState(false);
  const [isDismissed, setIsDismissed] = useState(() => {
    try {
      return localStorage.getItem(DISMISS_KEY) === 'true';
    } catch {
      return false;
    }
  });

  const subscribeMutation = useMutation({
    mutationFn: async () => {
      await optInService.subscribe('Marketing');
      // Refresh user to get updated credits and marketingOptIn status
      const updatedUser = await authService.getCurrentUser();
      useAuthStore.getState().setUser(updatedUser);
    },
  });

  // Don't show if user already opted in, banner was dismissed, or still loading
  if (!user || user.marketingOptIn === true || isDismissed || subscribeMutation.isSuccess) {
    return null;
  }

  const handleDismiss = () => {
    setIsDismissed(true);
    try {
      localStorage.setItem(DISMISS_KEY, 'true');
    } catch {
      // localStorage might be unavailable
    }
  };

  const handleSubscribe = () => {
    if (!consent) return;
    subscribeMutation.mutate();
  };

  // Show success state briefly before hiding
  if (subscribeMutation.isSuccess) {
    return null;
  }

  return (
    <div className="relative rounded-xl border border-[var(--color-primary-start)]/20 bg-gradient-to-r from-[var(--color-primary-start)]/5 to-[var(--color-primary-end)]/5 p-5 mb-6">
      {/* Dismiss button */}
      <button
        onClick={handleDismiss}
        className="absolute top-3 right-3 p-1 rounded-md text-[var(--color-charcoal)]/40 hover:text-[var(--color-charcoal)] hover:bg-black/5 transition-colors"
        aria-label={t('common.close')}
      >
        <X className="h-4 w-4" />
      </button>

      <div className="flex items-start gap-4">
        {/* Gift icon */}
        <div className="flex-shrink-0 mt-0.5 h-10 w-10 rounded-full bg-gradient-to-br from-[var(--color-primary-start)] to-[var(--color-primary-end)] flex items-center justify-center">
          <Gift className="h-5 w-5 text-white" />
        </div>

        <div className="flex-1 min-w-0 pr-4">
          {/* Title & description */}
          <p className="font-semibold text-[var(--color-charcoal)]">
            {t('dashboard.marketingBanner.title')}
          </p>
          <p className="text-sm text-[var(--color-charcoal)]/70 mt-1">
            {t('dashboard.marketingBanner.description')}
          </p>

          {/* Consent checkbox */}
          <div className="flex items-start gap-3 mt-4">
            <Checkbox
              id="dashboardMarketingConsent"
              checked={consent}
              onCheckedChange={(checked) => setConsent(checked === true)}
              disabled={subscribeMutation.isPending}
              className="mt-0.5"
            />
            <Label
              htmlFor="dashboardMarketingConsent"
              className="text-sm text-[var(--color-charcoal)]/70 leading-snug font-normal cursor-pointer"
            >
              <Trans
                i18nKey="auth.marketingConsentLabel"
                components={{
                  privacyLink: <a href="/datenschutz" target="_blank" rel="noopener noreferrer" className="gradient-text font-medium hover:opacity-80 transition-opacity underline" />,
                }}
              />
            </Label>
          </div>

          {/* Action buttons */}
          <div className="flex items-center gap-3 mt-4">
            <Button
              onClick={handleSubscribe}
              disabled={!consent || subscribeMutation.isPending}
              className="bg-gradient-to-r from-[var(--color-primary-start)] to-[var(--color-primary-end)] text-white hover:opacity-90 transition-opacity"
              size="sm"
            >
              {subscribeMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  {t('dashboard.marketingBanner.submitting')}
                </>
              ) : (
                t('dashboard.marketingBanner.claimCredit')
              )}
            </Button>
          </div>

          {/* Error state */}
          {subscribeMutation.isError && (
            <p className="text-sm text-red-600 mt-2">
              {t('dashboard.marketingBanner.error')}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
